import { texts } from "@utils/textMessages"
import { log } from "@utils/logger"
import { expandWhere, flattenWhere } from "@utils/search"
import Session, { useSessionContext } from "supertokens-auth-react/recipe/session"
import { appInfo, dvcApiDomain } from "config/appInfo"
import useSWR, { mutate } from "swr"
import { getQueryFromFilters } from "@components/Listings/ListingsFilter/store"
import { useCallback } from "react"

export type User = {
    username?: string
    first_name?: string
    last_name?: string
    birthdate: string
    gender: string
    emails?: string[]
    email_notification?: boolean
    push_notification?: ""
    isThirdParty: boolean
}
const baseURL = dvcApiDomain

export async function getUser() {
    let user
    let error
    let token
    try {
        if (await Session.doesSessionExist()) {
            user = await fetch(`${appInfo.apiDomain}/api/user`, { headers: { ...(typeof window === "undefined" && { "User-Agent": "dvc-client" }) } }).then((res) => res.json())

            const accessTokenPayload = await Session.getAccessTokenPayloadSecurely()
            if (accessTokenPayload.sub !== undefined) {
                token = await Session.getAccessToken()
            } else {
                // This branch is only required if there are valid access tokens created before the update
                // It can be removed after the validity period ends
                token = accessTokenPayload.jwt
            }
        } else {
            error = texts.notAuthenticated
        }
    } catch (err) {
        error = err.toString()
    }
    if (error) {
        return Promise.reject(error)
    }
    return { token, user }
}

export function useUser() {
    const { loading } = useSessionContext()
    const swrRes = useSWR<{ user: User; token: string }>(loading ? null : `${appInfo.apiDomain}/api/user`, getUser)
    return { ...swrRes, isAuthenticated: !!swrRes?.data?.user, user: swrRes.data?.user, isLoading: swrRes.isLoading || loading }
}

function normalizeSavedSearch(s: SavedSearch): SavedSearch {
    const where = { ...flattenWhere(s?.search_query?.where) }
    const sort = String(s?.search_query?.sort || "")
    let query = s?.search_query?.query
    //const resortIds = where?.resort?.short_name_in?.map((r) => resorts.find((resort) => resort.short_name === r)?.id.toString()) || []

    if (!query) {
        query = getQueryFromFilters({
            ...where,
            sort
        })
    }
    return { ...s, search_query: { ...s?.search_query, query, where, sort } }
}
export type Frequency = "asap" | "daily" | "weekly" | "2x-daily"

export type SavedSearch = {
    created_at: string
    name: string
    search_query: {
        query: string
        where: any
        sort: string
    }
    should_notify: number
    should_notify_push: string
    frequency: Frequency
}

async function fetchUserSearches(): Promise<SavedSearch[]> {
    const { token, user } = await getUser()
    try {
        const searches = await fetch(`${baseURL}/listings/searches/${user.username}`, {
            headers: {
                Authorization: token,
                ...(typeof window === "undefined" && { "User-Agent": "dvc-client" })
            }
        }).then((res) => res.json())
        return Array.isArray(searches) ? searches.map((s) => normalizeSavedSearch(s)) : []
    } catch (e: any) {
        log(e)
        return []
    }
}

export const useUserSearches = () => {
    const { user } = useUser()

    const { data, isLoading, mutate } = useSWR<SavedSearch[]>(user?.username ? `${baseURL}/listings/searches?u=${user.username}` : null, fetchUserSearches)

    const searches = Array.isArray(data) ? data : []

    return { searches, isLoading, mutate }
}

export async function saveUserSearches(body: SavedSearch) {
    const expandedBody = { ...body, search_query: { ...body?.search_query, where: { ...expandWhere(body?.search_query?.where) } } }
    try {
        const { token, user } = await getUser()
        const url = `${baseURL}/listings/searches?u=${user.username}`
        return await fetch(url, {
            method: "POST",
            headers: {
                Authorization: token,
                "Content-Type": "application/json",
                ...(typeof window === "undefined" && { "User-Agent": "dvc-client" })
            },
            body: JSON.stringify(expandedBody)
        })
            .then((res) => res.json())
            .finally(() => {
                mutate(url)
            })
    } catch (err) {
        log(err)
        throw new Error(texts.somethingWentWrong)
    }
}

export async function updateUserSearches(body: SavedSearch) {
    const expandedBody = { ...body, search_query: { ...body?.search_query, where: { ...expandWhere(body?.search_query?.where) } } }
    try {
        const { token, user } = await getUser()
        const url = `${baseURL}/listings/searches?u=${user.username}`
        return await fetch(url, {
            method: "PUT",
            headers: {
                Authorization: token,
                "Content-Type": "application/json",
                ...(typeof window === "undefined" && { "User-Agent": "dvc-client" })
            },
            body: JSON.stringify(expandedBody)
        })
            .then((res) => res.json())
            .finally(() => {
                mutate(url)
            })
    } catch (err) {
        log(err)
        throw new Error(texts.somethingWentWrong)
    }
}

export async function deleteUserSearches(created_at: string) {
    return await fetch(`${baseURL}/listings/searches/object/${created_at}`, {
        method: "DELETE",
        headers: {
            ...(typeof window === "undefined" && { "User-Agent": "dvc-client" })
        }
    }).then((res) => res.json())
}

export async function getSearchByCreatedAt(created_at: string): Promise<SavedSearch> {
    const search = await fetch(`${baseURL}/listings/searches/search/${created_at}`, { headers: { ...(typeof window === "undefined" && { "User-Agent": "dvc-client" }) } }).then(
        (res) => res.json()
    )
    return normalizeSavedSearch(search)
}

export function useSearchByCreatedAt(created_at: string) {
    const fetcher = useCallback(() => getSearchByCreatedAt(created_at), [created_at])
    return useSWR(`${baseURL}/listings/searches/search/${created_at}`, fetcher)
}

export function updateUserProps(newProps: Partial<{ email_notification: boolean; push_notifications?: string[] }>) {
    return fetch(`${appInfo.apiDomain}/api/user`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            ...(typeof window === "undefined" && { "User-Agent": "dvc-client" })
        },
        body: JSON.stringify(newProps)
    })
}
